import { useRef } from 'react';

import type { DashboardResult, DashboardResultType } from '@api/domain/types';
import { DashboardResultType as ResultType } from '@api/domain/types';

import { computePageOneMaxes } from '@v2/pages/dashboard/common/helpers';
import { DashboardListMode, type PageOneMaxes } from '@v2/pages/dashboard/common/types/list';

export interface UseManageDashboardListParams {
  items: DashboardResult[];
  resultType: DashboardResultType;
  pageNumber: number;
  resetSignature: string;
}

export interface UseManageDashboardListResult {
  mode: DashboardListMode;
  maxSeconds: number | null;
  maxPositive: number | null;
  maxNegative: number | null;
}

export const useManageDashboardList = ({
  items,
  resultType,
  pageNumber,
  resetSignature,
}: UseManageDashboardListParams): UseManageDashboardListResult => {
  const lastSignatureRef = useRef<string | null>(null);
  const maxesRef = useRef<PageOneMaxes>({ positive: null, negative: null });
  const maxSecondsRef = useRef<number | null>(null);

  if (lastSignatureRef.current !== resetSignature) {
    lastSignatureRef.current = resetSignature;
    maxesRef.current = { positive: null, negative: null };
    maxSecondsRef.current = null;
  }

  const mode: DashboardListMode =
    resultType === ResultType.Actual ? DashboardListMode.Actual : DashboardListMode.Diverging;

  if (pageNumber === 1 && items.length > 0) {
    if (mode === DashboardListMode.Actual && maxSecondsRef.current == null) {
      let max = 0;
      for (const item of items) {
        const v = Math.abs(item.totalTimeTotalInSeconds ?? 0);
        if (v > max) max = v;
      }
      maxSecondsRef.current = max || null;
    }
    if (mode === DashboardListMode.Diverging) {
      const computed = computePageOneMaxes(items);
      if (maxesRef.current.positive == null && computed.positive != null) {
        maxesRef.current = { ...maxesRef.current, positive: computed.positive };
      }
      if (maxesRef.current.negative == null && computed.negative != null) {
        maxesRef.current = { ...maxesRef.current, negative: computed.negative };
      }
    }
  }

  return {
    mode,
    maxSeconds: maxSecondsRef.current,
    maxPositive: maxesRef.current.positive,
    maxNegative: maxesRef.current.negative,
  };
};
